import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  createInitialMockMeals,
  createInitialMockSupplements,
} from '../data/mealTrackingMockData';
import type {
  MealEntry,
  MealFormValues,
  MealPhoto,
  SupplementEntry,
  SupplementFormValues,
} from '../types/mealTracking';
import {
  createMealId,
  createPhotoId,
  createSupplementId,
  sortMealsChronologically,
  sortSupplementsChronologically,
} from '../pages/MealTracking/mealTracking.utils';

interface MealTrackingContextValue {
  meals: MealEntry[];
  supplements: SupplementEntry[];
  getMealsByDate: (date: string) => MealEntry[];
  getMealById: (mealId: string) => MealEntry | undefined;
  createMeal: (values: MealFormValues) => MealEntry;
  updateMeal: (mealId: string, values: MealFormValues) => MealEntry | null;
  deleteMeal: (mealId: string) => void;
  removePhotoFromMeal: (mealId: string, photoId: string) => void;
  getSupplementsByDate: (date: string) => SupplementEntry[];
  getSupplementById: (supplementId: string) => SupplementEntry | undefined;
  createSupplement: (values: SupplementFormValues) => SupplementEntry;
  updateSupplement: (supplementId: string, values: SupplementFormValues) => SupplementEntry | null;
  deleteSupplement: (supplementId: string) => void;
}

const MealTrackingContext = createContext<MealTrackingContextValue | null>(null);

const clonePhotos = (photos: MealPhoto[]): MealPhoto[] => photos.map((photo) => ({ ...photo }));

const revokeLocalPhoto = (photo: MealPhoto) => {
  if (!photo.isDemoAsset && photo.previewUrl.startsWith('blob:')) {
    URL.revokeObjectURL(photo.previewUrl);
  }
};

const buildMealFromForm = (values: MealFormValues, existing?: MealEntry): MealEntry => {
  const now = new Date().toISOString();
  const customTitle = values.categoryId === 'other' ? values.customTitle.trim() : null;

  return {
    id: existing?.id ?? createMealId(),
    date: values.date,
    time: values.time.trim(),
    categoryId: values.categoryId,
    customTitle,
    description: values.description.trim(),
    photos: clonePhotos(values.photos),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };
};

const buildSupplementFromForm = (
  values: SupplementFormValues,
  existing?: SupplementEntry,
): SupplementEntry => {
  const now = new Date().toISOString();

  return {
    id: existing?.id ?? createSupplementId(),
    name: values.name.trim(),
    amount: values.amount.trim(),
    unitId: values.unitId,
    customUnit: values.unitId === 'other' ? values.customUnit.trim() : null,
    date: values.date,
    time: values.time.trim(),
    frequencyId: values.frequencyId,
    customFrequency: values.frequencyId === 'custom' ? values.customFrequency.trim() : null,
    brand: values.brand.trim(),
    notes: values.notes.trim(),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };
};

export const MealTrackingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [meals, setMeals] = useState<MealEntry[]>(() => createInitialMockMeals());
  const [supplements, setSupplements] = useState<SupplementEntry[]>(() => createInitialMockSupplements());

  const getMealsByDate = useCallback((date: string) => (
    sortMealsChronologically(meals.filter((meal) => meal.date === date))
  ), [meals]);

  const getMealById = useCallback((mealId: string) => (
    meals.find((meal) => meal.id === mealId)
  ), [meals]);

  const createMeal = useCallback((values: MealFormValues) => {
    const meal = buildMealFromForm(values);
    setMeals((current) => [...current, meal]);
    return meal;
  }, []);

  const updateMeal = useCallback((mealId: string, values: MealFormValues) => {
    let updated: MealEntry | null = null;

    setMeals((current) => current.map((meal) => {
      if (meal.id !== mealId) {
        return meal;
      }

      const removedPhotos = meal.photos.filter(
        (photo) => !values.photos.some((nextPhoto) => nextPhoto.id === photo.id),
      );
      removedPhotos.forEach(revokeLocalPhoto);

      updated = buildMealFromForm(values, meal);
      return updated;
    }));

    return updated;
  }, []);

  const deleteMeal = useCallback((mealId: string) => {
    setMeals((current) => {
      const target = current.find((meal) => meal.id === mealId);
      target?.photos.forEach(revokeLocalPhoto);
      return current.filter((meal) => meal.id !== mealId);
    });
  }, []);

  const removePhotoFromMeal = useCallback((mealId: string, photoId: string) => {
    setMeals((current) => current.map((meal) => {
      if (meal.id !== mealId) {
        return meal;
      }

      const photo = meal.photos.find((item) => item.id === photoId);
      if (photo) {
        revokeLocalPhoto(photo);
      }

      return {
        ...meal,
        photos: meal.photos.filter((item) => item.id !== photoId),
        updatedAt: new Date().toISOString(),
      };
    }));
  }, []);

  const getSupplementsByDate = useCallback((date: string) => (
    sortSupplementsChronologically(supplements.filter((item) => item.date === date))
  ), [supplements]);

  const getSupplementById = useCallback((supplementId: string) => (
    supplements.find((item) => item.id === supplementId)
  ), [supplements]);

  const createSupplement = useCallback((values: SupplementFormValues) => {
    const entry = buildSupplementFromForm(values);
    setSupplements((current) => [...current, entry]);
    return entry;
  }, []);

  const updateSupplement = useCallback((supplementId: string, values: SupplementFormValues) => {
    let updated: SupplementEntry | null = null;

    setSupplements((current) => current.map((item) => {
      if (item.id !== supplementId) {
        return item;
      }

      updated = buildSupplementFromForm(values, item);
      return updated;
    }));

    return updated;
  }, []);

  const deleteSupplement = useCallback((supplementId: string) => {
    setSupplements((current) => current.filter((item) => item.id !== supplementId));
  }, []);

  const value = useMemo(() => ({
    meals,
    supplements,
    getMealsByDate,
    getMealById,
    createMeal,
    updateMeal,
    deleteMeal,
    removePhotoFromMeal,
    getSupplementsByDate,
    getSupplementById,
    createSupplement,
    updateSupplement,
    deleteSupplement,
  }), [
    meals,
    supplements,
    getMealsByDate,
    getMealById,
    createMeal,
    updateMeal,
    deleteMeal,
    removePhotoFromMeal,
    getSupplementsByDate,
    getSupplementById,
    createSupplement,
    updateSupplement,
    deleteSupplement,
  ]);

  return (
    <MealTrackingContext.Provider value={value}>
      {children}
    </MealTrackingContext.Provider>
  );
};

export const useMealTracking = (): MealTrackingContextValue => {
  const context = useContext(MealTrackingContext);
  if (!context) {
    throw new Error('useMealTracking must be used within MealTrackingProvider');
  }

  return context;
};

export const createLocalMealPhoto = (file: File): MealPhoto => ({
  id: createPhotoId(),
  previewUrl: URL.createObjectURL(file),
  fileName: file.name,
  file,
});
